import React, { useRef, useEffect, useMemo, useState } from "react";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import pl from "react-phone-input-2/lang/pl.json";
import tr from "react-phone-input-2/lang/tr.json";
import ar from "react-phone-input-2/lang/ar.json";
import cs from "../../../i18n/cs/countries.json";
import es from "react-phone-input-2/lang/es.json";
import de from "react-phone-input-2/lang/de.json";
import ja from "react-phone-input-2/lang/jp.json";

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const LabelledMultiline = styled.div`
  color: ${(props) => props?.nero};
  font-size: 15px;
  font-weight: 600;
  word-break: break-all;
`;

const Input = styled.input`
  font-weight: 600 !important;
  height: auto !important;
  padding: 0 !important;
  color: ${(props) => props?.gray};
  border: none;
  border-radius: 0;
  border-bottom: 2px solid
    ${(props) => (props.hasError ? props.theme?.cardinal : props.theme?.gray)} !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  text-align: left;

  &:hover,
  &:focus {
    border-bottom: 2px solid
      ${(props) =>
        props.hasError
          ? props.theme?.cardinal
          : props.greenTheme
          ? props.theme?.yellowGreen
          : props.theme?.darkPurple} !important;
  }
`;

const PhoneInputWrapper = styled.div`
  .form-control {
    border-bottom: none;
    margin-left: 8px;
  }

  & input {
    font-weight: 600 !important;
    left: 37px;
    width: calc(100% - 37px) !important;
    font-size: 1rem !important;
    border-top: 0px solid !important;
    border-right: 0px solid !important;
    border-radius: 0px !important;
    background: transparent !important;
    height: auto !important;
    padding: 0 !important;
    text-align: left;
    border-bottom: 2px solid
      ${(props) =>
        props.hasError
          ? props.theme?.cardinal
          : props.greenTheme
          ? props.theme?.yellowGreen
          : props.theme?.gray} !important;

    &:hover,
    &:focus {
      border-bottom: 2px solid
        ${(props) =>
          props.hasError
            ? props.theme?.cardinal
            : props.greenTheme
            ? props.theme?.yellowGreen
            : props.theme?.darkPurple} !important;
    }

    &[readonly],
    &[disabled] {
      color: ${(props) => props.theme?.nero} !important;
      cursor: default;
      border-bottom: 0px !important;
      background: transparent !important;
    }
  }

  & .flag-dropdown {
    border: 0px !important;

    background-color: transparent;

    .selected-flag {
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .country-list {
      direction: ${(props) => props?.isRtl && "rtl"};
      width: max-content;
      .country {
        display: flex;
        text-align: ${(props) => props?.isRtl && "left"};
        flex-direction: ${(props) => props?.isRtl && "row-reverse"};
        gap: 3px;
      }
    }
  }

  &:focus {
    box-shadow: 0 0.2rem 1rem transparent;
  }
`;

const Error = styled.span`
  color: ${(props) => props.theme?.cardinal};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const jsonImportMap = { es, pl, tr, ar, cs, de, ja };

const LabelledInput = (props) => {
  const {
    className,
    title,
    value,
    onChange,
    onCopy,
    onDrag,
    onDrop,
    onPaste,
    onBlur,
    onInput,
    type,
    country,
    hasError,
    errorMessage,
    pattern,
    placeholder,
    readOnly,
    disabled,
    autoFocus,
    greenTheme,
    locale,
    tabIndex,
    noTitle,
    onKeyDown,
    isRtl,
  } = props;

  const inputRef = useRef();

  /*
  Need this to avoid phone number being cleanned - lib bug
  */

  const [phoneProp, setPhoneProp] = useState(value);

  useEffect(() => {
    setPhoneProp(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 1);
    }
  }, [autoFocus]);

  const isReadOnlyElement = readOnly ? { readOnly: true, disabled: true } : "";

  const localization = useMemo(
    () => jsonImportMap[locale?.substring(0, 2)] || null,
    [locale]
  );

  return (
    <div className={className}>
      {!noTitle && <Label>{title}</Label>}
      {type === "tel" ? (
        <PhoneInputWrapper data-testid={props["data-testid"]} isRtl={isRtl}>
          <PhoneInput
            key={locale}
            country={country}
            hasError={hasError}
            {...(localization && { ...{ localization } })}
            type={type}
            countryCodeEditable={false}
            className="form-control phone-input"
            value={phoneProp}
            placeholder={placeholder}
            onChange={(value, data, event, formattedValue) => {
              onChange(value, data, event, formattedValue);
              if (inputRef) {
                inputRef.current.focus();
              }
            }}
            pattern={pattern}
            inputProps={{
              ref: inputRef,
            }}
            {...isReadOnlyElement}
          />
        </PhoneInputWrapper>
      ) : readOnly ? (
        <LabelledMultiline data-testid={props["data-testid"]}>
          {value}
        </LabelledMultiline>
      ) : (
        <Input
          tabIndex={tabIndex}
          ref={inputRef}
          hasError={hasError}
          data-testid={props["data-testid"]}
          type={type}
          className="form-control"
          value={value || ""}
          placeholder={placeholder}
          onChange={onChange}
          onCopy={onCopy}
          onDrag={onDrag}
          onDrop={onDrop}
          onPaste={onPaste}
          onInput={onInput}
          pattern={pattern}
          onBlur={onBlur}
          disabled={disabled}
          greenTheme={greenTheme}
          onKeyDown={onKeyDown}
        />
      )}
      {hasError && errorMessage && (
        <Error data-testid="error-message">{errorMessage}</Error>
      )}
    </div>
  );
};

LabelledInput.defaultProps = {
  className: "",
  type: "text",
  hasError: false,
  errorMessage: null,
  pattern: null,
  onChange: null,
  onInput: null,
  onBlur: null,
  placeholder: "",
  disabled: false,
  autoFocus: false,
  tabIndex: null,
  onKeyDown: null,
};

LabelledInput.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onInput: PropTypes.func,
  type: PropTypes.oneOf(["text", "number", "tel", "email", "password"]),
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  tabIndex: PropTypes.number,
  onKeyDown: PropTypes.func,
};

export default withTheme(LabelledInput);
